import { useState } from "react";
import { useLang, ensureSessionId } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PreToolDisclaimer from "@/components/PreToolDisclaimer";

const QS = [
  { key: "urgency", opts: ["low", "medium", "high"] },
  { key: "domestic_violence_indicators", opts: ["no", "yes", "unclear"] },
  { key: "coercion_indicators", opts: ["no", "yes", "unclear"] },
  { key: "power_balance", opts: ["balanced", "somewhat_imbalanced", "significantly_imbalanced"] },
  { key: "withholding_documents", opts: ["no", "yes", "unclear"] },
  { key: "immigration_dependency", opts: ["no", "yes", "unclear"] },
  { key: "risk_renewed_removal", opts: ["no", "yes", "unclear"] },
  { key: "voluntary_participation", opts: ["yes", "no", "unclear"] },
  { key: "independent_legal_advice", opts: ["yes", "no", "unclear"] },
  { key: "pending_proceedings", opts: ["no", "yes", "unclear"] },
  { key: "communication_barriers", opts: ["no", "yes", "unclear"] },
  { key: "language_barriers", opts: ["no", "yes", "unclear"] },
  { key: "safety_planning_needed", opts: ["no", "yes", "unclear"] },
];

export default function Mediation() {
  const { t, lang } = useLang();
  const [a, setA] = useState({});
  const [res, setRes] = useState(null);

  const submit = async () => {
    try {
      const r = await api.post("/mediation-suitability", { ...a, language: lang, session_id: ensureSessionId() });
      setRes(r.data.result); window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { toast.error("Could not generate suitability screening."); }
  };

  const flagIcon = (f) => f === "priority"
    ? <AlertTriangle className="w-4 h-4 text-amber-400" />
    : <AlertTriangle className="w-4 h-4 text-blue-300" />;

  return (
    <div className="max-w-5xl mx-auto px-5 lg:px-8 py-20" data-testid="mediation-page">
      <h1 className="text-4xl sm:text-5xl font-bold">{t.mediation.title}</h1>
      <p className="mt-4 text-slate-300 max-w-3xl">{t.mediation.sub}</p>
      <p className="mt-3 text-sm text-slate-500 italic max-w-3xl">{t.mediation.framing}</p>

      <PreToolDisclaimer showHague />

      {res ? (
        <div className="mt-10 card-mift p-6" data-testid="mediation-result">
          <h2 className="text-2xl font-semibold text-blue-300">{t.mediation.result_title}</h2>
          <div className="mt-3 inline-block text-sm px-3 py-1 rounded-full border border-blue-500/40 text-blue-300 bg-blue-500/10"
               data-testid="mediation-outcome">
            {t.mediation.outcomes[res.outcome]}
          </div>
          <div className="mt-6 space-y-3">
            {res.factors.map((f, i) => (
              <div key={i} className="flex items-start gap-3 border border-slate-800 rounded-lg p-4">
                {flagIcon(f.flag)}
                <div>
                  <div className="font-semibold text-slate-100 text-sm">{f.factor}</div>
                  <div className="text-sm text-slate-300 mt-1">{f.note}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <h3 className="font-semibold text-slate-100">Recommended safeguards</h3>
            <ul className="mt-3 space-y-2 text-sm text-slate-300">
              {res.recommended_safeguards.map((s, i) => <li key={i}>→ {s}</li>)}
            </ul>
          </div>
          <p className="mt-8 text-xs text-slate-500 italic">{res.disclaimer}</p>
          <button className="mt-6 btn-ghost" onClick={() => { setRes(null); setA({}); }} data-testid="mediation-restart">
            {t.mediation.restart}
          </button>
        </div>
      ) : (
        <div className="mt-10 grid gap-5">
          {QS.map((q, i) => (
            <div key={q.key} className="card-mift p-5" data-testid={`mq-${q.key}`}>
              <div className="text-xs text-slate-500 uppercase tracking-wider">Q{i + 1}</div>
              <div className="mt-1 text-slate-100 font-medium">{t.mediation.q[q.key]}</div>
              <div className="mt-4 max-w-md">
                <Select value={a[q.key] || ""} onValueChange={(v) => setA((s) => ({ ...s, [q.key]: v }))}>
                  <SelectTrigger data-testid={`mselect-${q.key}`} className="bg-[#0A1224] border-slate-700"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-[#0A1224] border-slate-700 text-slate-100">
                    {q.opts.map((o) => (
                      <SelectItem key={o} value={o} data-testid={`mopt-${q.key}-${o}`}>{t.assessment.opts[o] || t.mediation.opts?.[o] || o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
          <button onClick={submit} disabled={QS.some((q) => !a[q.key])}
                  className="btn-primary w-max disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="mediation-submit">
            {t.mediation.submit}
          </button>
        </div>
      )}
    </div>
  );
}
